import {
  AnswersGroupingType,
  getProviderIdsFromFilter as getProviderOrganizationIdsFromFilter,
  removeQuestionFilters,
  transformFilterNamesForInquiries,
} from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import { uniq } from 'lodash'
import endpoints from '../api/endpoints'
import { FetchKey, useFetchCount, useFetchFacets } from '../api/fetchHooks'
import type { FacetItem } from '../pages/ResourceCollection'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import type {
  StatisticsSummary,
  StatisticsSummaryPerGroup,
} from '../pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/DataHubSummaryChart'
import type { StatisticsForQuestionPerGroup } from '../pages/ResourceCollection/Collections/DataHubScene'
import type { FilterGroup } from '../pages/ResourceCollection/Filters/useFilters'
import { Operators } from '../pages/ResourceCollection/Filters/useFilters'
import { InquiryStatus } from '../types/resourceExplorer'
import { comparePeriods } from '../utils/getOrderedPeriods'
import { equalsCaseInsensitive, insertIf } from '../utils/helpersTs'
import { SpendClassification } from '../wf-constants'
import { getPeriodsToShowInStatisticsView } from './usePeriodName'

const getTotalPeriods = (
  statistics: StatisticsForQuestionPerGroup,
  unansweredInquiriesByPeriodFacets: FacetItem[],
): string[] => {
  const totalPeriods = uniq([
    ...statistics.resultForGroups.map(r => r.groupName),
    ...unansweredInquiriesByPeriodFacets.filter(r => r?.value).map(r => r.value.toString()),
  ]).sort(comparePeriods)
  return getPeriodsToShowInStatisticsView(totalPeriods)
}
const toPeriodStatisticsSummaryResult = (
  statistics: StatisticsForQuestionPerGroup,
  unansweredInquiriesByPeriodFacets: FacetItem[],
): StatisticsSummary => {
  const totalPeriods = getTotalPeriods(statistics, unansweredInquiriesByPeriodFacets)
  const statisticsSummaryResult: StatisticsSummaryPerGroup[] = totalPeriods.map(period => {
    const resultGroup = statistics.resultForGroups.find(r => r?.groupName === period)
    const unansweredQuestionCount = unansweredInquiriesByPeriodFacets.find(p => p?.value === period)?.count ?? 0

    return {
      groupName: period,
      groupId: resultGroup?.groupId,
      companiesAnswered: resultGroup?.questionMonitoring?.companiesHaveAnswered?.number ?? 0,
      companiesAnsweredDontHaveThis: resultGroup?.questionMonitoring?.companiesHaveNotAnswered?.number ?? 0,
      companiesNotAnswered: unansweredQuestionCount,
    }
  })

  return {
    id: statistics.id,
    title: statistics.title,
    resultForGroups: statisticsSummaryResult,
  }
}

const getTotalSpendClassifications = (
  statistics: StatisticsForQuestionPerGroup,
  unansweredInquiriesWithSpendsFacets: FacetItem[],
): string[] => {
  const totalSpendIntervals = uniq([
    ...statistics.resultForGroups.map(r => r.groupName),
    ...(unansweredInquiriesWithSpendsFacets?.filter(r => r?.value).map(r => r.value.toString()) ?? []),
  ])
  return totalSpendIntervals
}
const toSpendStatisticsSummaryResult = (
  statistics: StatisticsForQuestionPerGroup,
  unansweredInquiriesWithSpendsFacets: FacetItem[],
  unansweredInquiriesWithSpendsFacetsCount: number,
  unansweredInquiriesCount: number,
): StatisticsSummary => {
  const totalSpendIntervals = getTotalSpendClassifications(statistics, unansweredInquiriesWithSpendsFacets)

  const statisticsSummaryResult: StatisticsSummaryPerGroup[] = totalSpendIntervals.map(spendInterval => {
    const resultGroup = statistics.resultForGroups.find(r => r?.groupName === spendInterval)
    const unansweredQuestionCount =
      unansweredInquiriesWithSpendsFacets.find(p => p?.value === spendInterval)?.count ?? 0

    return {
      groupName: spendInterval,
      groupId: resultGroup?.groupId,
      companiesAnswered: resultGroup?.questionMonitoring?.companiesHaveAnswered?.number ?? 0,
      companiesAnsweredDontHaveThis: resultGroup?.questionMonitoring?.companiesHaveNotAnswered?.number ?? 0,
      companiesNotAnswered:
        spendInterval === SpendClassification.NotSet
          ? unansweredInquiriesCount - unansweredInquiriesWithSpendsFacetsCount
          : unansweredQuestionCount,
    }
  })

  return {
    id: statistics.id,
    title: statistics.title,
    resultForGroups: statisticsSummaryResult,
  }
}

const isSingleQuestionnaireFilter = (filterGroup: FilterGroup) => {
  if (!equalsCaseInsensitive(filterGroup.name, 'template.id')) return false
  const filterValue = filterGroup.filters?.[0]?.value
  if (!Array.isArray(filterValue)) return false
  return filterValue.length === 1
}

export const useStatisticsResultByGroup = (
  statistics: StatisticsForQuestionPerGroup,
  groupBy: AnswersGroupingType,
  userFilters: FilterGroup[],
) => {
  const cleanFilters = removeQuestionFilters(userFilters)
  const renamedFiltersForInquiries = transformFilterNamesForInquiries(cleanFilters)
  const providerOrganizationIds = getProviderOrganizationIdsFromFilter(userFilters)
  const periodFilterValue = userFilters.find(x => x.name === 'response.request.periodName')?.filters[0]?.value

  const singleQuestionnaireFilterExists = renamedFiltersForInquiries?.some(isSingleQuestionnaireFilter) ?? false

  const facetsFilters = [
    ...renamedFiltersForInquiries,
    ...insertIf(!singleQuestionnaireFilterExists, {
      name: 'template.sections.questions.id',
      filters: [
        {
          operator: Operators.EqualTo,
          value: statistics.id,
        },
      ],
    }),
    {
      name: 'status',
      filters: [{ operator: Operators.EqualTo, value: InquiryStatus.Requested }],
    },
    ...insertIf(Boolean(providerOrganizationIds), {
      name: 'provider.organizationId',
      filters: [
        {
          operator: Operators.In,
          value: providerOrganizationIds,
        },
      ],
    }),
  ]

  const { facets: unansweredInquiriesByPeriodFacets, isLoading: isLoadingUnansweredInquiriesByPeriodFacets } =
    useFetchFacets({
      key: FetchKey.Inquiry,
      endpoint: endpoints.inquiryWithFacets,
      facetsParam: [{ name: 'periodName', isEnum: true }],
      filter: facetsFilters,
      skipNotSet: true,
      options: { staleTime: ONE_MINUTE_MS, enabled: groupBy === AnswersGroupingType.PeriodName },
    })

  const { count: unansweredInquiriesCount, isLoading: isUnansweredInquiriesCountLoading } = useFetchCount({
    key: FetchKey.InquiryCount,
    endpoint: endpoints.inquiryCount,
    payload: facetsFilters,
    options: { staleTime: ONE_MINUTE_MS, enabled: groupBy === AnswersGroupingType.SpendClassification },
  })

  const {
    facets: unansweredInquiriesWithSpendsFacet,
    count: unansweredInquiriesWithSpendsFacetCount,
    isLoading: isLoadingUnansweredInquiriesWithSpendsFacet,
  } = useFetchFacets({
    key: FetchKey.Inquiry,
    endpoint: endpoints.inquiryWithFacets,
    facetsParam: [{ name: 'provider.spends.spendClassification' }],
    filter: [
      ...facetsFilters,
      ...insertIf(Boolean(periodFilterValue), {
        name: 'provider.spends.periodName',
        filters: [
          {
            operator: Operators.In,
            value: Array.isArray(periodFilterValue) ? periodFilterValue : [String(periodFilterValue)],
          },
        ],
      }),
    ],
    skipNotSet: true,
    options: { staleTime: ONE_MINUTE_MS, enabled: groupBy === AnswersGroupingType.SpendClassification },
  })

  const isLoading =
    isLoadingUnansweredInquiriesByPeriodFacets ||
    isUnansweredInquiriesCountLoading ||
    isLoadingUnansweredInquiriesWithSpendsFacet

  const periodStatisticSummaryResult = toPeriodStatisticsSummaryResult(
    statistics,
    unansweredInquiriesByPeriodFacets.flat(),
  )
  const spendStatisticSummaryResult = toSpendStatisticsSummaryResult(
    statistics,
    unansweredInquiriesWithSpendsFacet.flat(),
    unansweredInquiriesWithSpendsFacetCount,
    unansweredInquiriesCount ?? 0,
  )

  return { periodStatisticSummaryResult, spendStatisticSummaryResult, isLoading }
}
